import { useEffect, useRef } from "react";

/**
 * Calls `onArrival` with what a newer answer brought that no earlier answer
 * had — never with what stood there on the first one. That is the page
 * itself, read in order; a live region that talks over it on every visit is
 * noise (UX ruling 10, F-13). Each id is announced once, however often the
 * list is fetched again (`useIncomingHandovers` polls once a minute).
 *
 * `items` is the query's own array, `undefined` while nothing has arrived —
 * its identity changes only when an answer does. `idOf` must be stable
 * (a module-level function), or every render compares again.
 *
 * A short delay before speaking: a region inserted in the same frame as its
 * text is not reliably read out (`RecordHead` waits the same 50 ms). The
 * timer survives the next render and is only dropped with the component, so
 * a quick second render cannot swallow an announcement.
 */
export function useArrivals<T>(
  items: readonly T[] | undefined,
  idOf: (item: T) => string,
  onArrival: (arrived: T[]) => void,
): void {
  const known = useRef<Set<string> | null>(null);
  const speak = useRef(onArrival);
  const timers = useRef<number[]>([]);

  useEffect(() => {
    speak.current = onArrival;
  });

  useEffect(() => {
    if (items === undefined) return;
    if (known.current === null) {
      known.current = new Set(items.map(idOf));
      return;
    }
    const seen = known.current;
    const arrived = items.filter((item) => !seen.has(idOf(item)));
    if (arrived.length === 0) return;
    for (const item of arrived) seen.add(idOf(item));
    timers.current.push(window.setTimeout(() => speak.current(arrived), 50));
  }, [items, idOf]);

  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending) window.clearTimeout(timer);
    };
  }, []);
}
